import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Button, Divider, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { seconds2Time } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import {
  initOrderInfo,
  updateOrderInfo,
  toggleOverStayModalVisible,
  initOverStayList,
} from '@/redux/actions/bill/settle';
import { parseFen2Currency } from '@/utils/number';

import CardsDrawer from '@/components/Cards';
import CheckCardModal from '@/components/Cards/check';
import { DoorLockCode } from '@/pages/device/card/interface';
import DeleteDoorCardModal from '@/components/Cards/components/DeleteDoorCardModal';
import EditableTable from '@/components/EditableTable';
import RenewalRecord, { RenewalRecordRef } from '@/components/Reservation/RenewalRecord';
import OvertimePriceAdjustList from './components/OvertimePriceAdjustList';

import { ROOM_OCC_TYPES, ROOM_OCC_DURATION } from '../config';
import commonStyles from '../common.module.scss';
import OverStayModal from './overStayModal';
import styles from './index.module.scss';

function CheckOut(props) {
  const defaultCardInfo = {
    recNo: '',
    lockNo: '',
    roomId: 0,
    roomNo: '',
    checkInTime: '',
    checkOutTime: '',
  };

  const { resvNo, orderInfoLoading, orderInfo, roomCardEnabled, adjustReasons } = props;
  const { cancelReservationList, checkOutRoomList } = orderInfo;

  const [cardInfo, setCardInfo] = useState(defaultCardInfo);
  const [roomIds, setRoomIds] = useState([]);
  const [cardsDrawerVisible, setCardsDrawerVisible] = useState(false);
  const [checkModalVisibile, setCheckModalVisibile] = useState(false);
  const renewalRecordRef = useRef<RenewalRecordRef>();

  // 预订违约金改变回调
  const saveModifiedCancalResvList = (modifiedList) => {
    props.updateOrderInfo({
      orderInfo: {
        ...orderInfo,
        cancelReservationList: modifiedList,
      },
    });
  };

  // 加收超时房费改变回调
  const saveModifiedCheckoutRoomList = (modifiedList) => {
    props.updateOrderInfo({
      orderInfo: {
        ...orderInfo,
        checkOutRoomList: modifiedList,
      },
    });
  };

  const deleteRef = useRef<any>();

  const checkRoomCard = () => {
    if (props?.enabledVendor?.lockCode === DoorLockCode.BE_LINE) {
      deleteRef.current?.openModal();
    } else {
      setCheckModalVisibile(true);
    }
  };

  const editRoomCard = (record) => {
    setCardInfo({
      recNo: record.recNo,
      lockNo: record.lockNo,
      roomId: record.roomId,
      roomNo: record.roomNo,
      checkInTime: record.checkinTime,
      checkOutTime: record.expectedDepartureTime,
    });
    setCardsDrawerVisible(true);
  };

  const overstayDetail = (data) => {
    // props.initOverStayList({
    //   recNo: data.recNo,
    // });
    // props.toggleOverStayModalVisible(true);
    renewalRecordRef.current.openModal(data.recNo);
  };

  const cancalResvColumns = [
    {
      title: intl.get('app_constants_room_type'),
      dataIndex: 'roomTypeName',
      key: 'roomTypeName',
      render: (v) => v || '-',
    },
    {
      title: intl.get('app_constants_room_no'),
      dataIndex: 'roomNo',
      key: 'roomNo',
      render: (v) => v || '-',
    },
    {
      title: intl.get('app_reservation_occ_type'),
      dataIndex: 'occType',
      key: 'occType',
      render: (v, r) => {
        if (r.extensionFlag) return intl.get('bill_settle_various');
        return ROOM_OCC_TYPES[v] || '-';
      },
    },
    {
      title: intl.get('app_reservation_eta_time'),
      dataIndex: 'expectedArrivalTime',
      key: 'expectedArrivalTime',
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('app_reservation_etd_time'),
      dataIndex: 'expectedDepartureTime',
      key: 'expectedDepartureTime',
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('app_reservation_occ_duration'),
      dataIndex: 'occDuration',
      key: 'occDuration',
      render: (v, record) =>
        v
          ? `${v}${ROOM_OCC_DURATION[record.occType] || intl.get('app_enum_occ_type_day_unit')}`
          : '-',
    },
    {
      title: intl.get('drawer_reserve_cancel_room_column_damages'),
      dataIndex: 'liquidatedDamages',
      key: 'liquidatedDamages',
      width: 150,
      align: 'right',
      editable: true,
      isPriceData: true,
      render: (val) => parseFen2Currency(val || 0),
    },
  ];

  const checkOutRoomColumns = [
    {
      title: intl.get('app_constants_room_type'),
      dataIndex: 'roomTypeName',
      key: 'roomTypeName',
      render: (v) => v || '-',
    },
    {
      title: intl.get('app_constants_room_no'),
      dataIndex: 'roomNo',
      key: 'roomNo',
      render: (v) => v || '-',
    },
    {
      title: intl.get('app_reservation_occ_type'),
      dataIndex: 'occType',
      key: 'occType',
      render: (v) => ROOM_OCC_TYPES[v] || '-',
    },
    {
      title: intl.get('drawer_checkout_room_column_card_count'),
      dataIndex: 'roomCardCount',
      key: 'roomCardCount',
    },
    {
      title: intl.get('app_reservation_checkin_time'),
      dataIndex: 'checkinTime',
      key: 'checkinTime',
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('app_reservation_etd_time'),
      dataIndex: 'expectedDepartureTime',
      key: 'expectedDepartureTime',
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('app_reservation_occ_duration'),
      dataIndex: 'occDuration',
      key: 'occDuration',
      render: (v, r) => {
        if (r.extensionFlag)
          return intl.get('app_enum_occ_type_multi_value', { value: r.occDuration });
        return v
          ? `${v}${ROOM_OCC_DURATION[r.occType] || intl.get('app_enum_occ_type_day_unit')}`
          : '-';
      },
    },
    {
      title: intl.get('drawer_checkout_room_column_overtime_value'),
      dataIndex: 'overtime',
      key: 'overtime',
      render: (v) => seconds2Time(v) || 0,
    },
    {
      title: intl.get('drawer_checkout_room_column_overtime_price'),
      dataIndex: 'overtimePriceReceivable',
      key: 'overtimePriceReceivable',
      width: 150,
      align: 'right',
      filterDropdown: true,
      filterIcon: (
        <Tooltip
          title={intl.get('drawer_checkout_room_column_overtime_price_tips')}
          placement="top"
        >
          <InfoCircleOutlined />
        </Tooltip>
      ),
      // editable: true,
      // isPriceData: true,
      render: (val) => parseFen2Currency(val || 0),
    },
    {
      title: intl.get('drawer_checkout_room_column_overtime_price_actual'),
      dataIndex: 'overtimePrice',
      key: 'overtimePrice',
      align: 'right',
      width: 240,
      // render: (value, record) => renderPriceItem(value, record),
      editable: true,
      isPriceData: true,
      render: (val) => parseFen2Currency(val || 0),
    },
    {
      title: intl.get('app_reservation_checkin_remark'),
      dataIndex: 'checkinRemark',
      key: 'checkinRemark',
      render: (value) => value || '—',
    },
    {
      title: intl.get('app_table_column_action'),
      width: 200,
      align: 'right',
      render: (_, record) => {
        if (!record.extensionFlag && record.roomCardCount === 0) return '-';

        return (
          <>
            {roomCardEnabled && record.roomCardCount > 0 ? (
              <>
                <Button type="link" onClick={() => editRoomCard(record)}>
                  {intl.get('drawer_exchange_table_content_card_manage')}
                </Button>
                <Divider style={{ margin: 0 }} type="vertical" />
              </>
            ) : null}
            <Button style={{ paddingRight: 0 }} onClick={() => overstayDetail(record)} type="link">
              {intl.get('drawer_checkout_room_content_extension_detail_btn')}
            </Button>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (checkOutRoomList.length > 0) {
      setRoomIds(checkOutRoomList.map((item) => item.roomId));
    }
  }, [checkOutRoomList]);

  return (
    <div className={styles['settle-order-info']}>
      <OverStayModal />

      {props?.enabledVendor?.lockCode === DoorLockCode.BE_LINE ? (
        <DeleteDoorCardModal
          innerRef={deleteRef}
          enabledVendor={props.enabledVendor}
          roomIds={roomIds}
          roomNo={cardInfo?.roomNo}
          checkCallBack={() => {
            deleteRef.current.closeModal();
            props.initOrderInfo({
              resvNo,
            });
          }}
        />
      ) : (
        <CheckCardModal
          checkModalVisibile={checkModalVisibile}
          roomIds={roomIds}
          checkCallBack={() => {
            setCheckModalVisibile(false);
            props.initOrderInfo({
              resvNo,
            });
          }}
        />
      )}
      <CardsDrawer
        cardsDrawerVisible={cardsDrawerVisible}
        recNo={cardInfo.recNo}
        roomId={cardInfo.roomId}
        roomNo={cardInfo.roomNo}
        checkInTime={cardInfo.checkInTime}
        checkOutTime={cardInfo.checkOutTime}
        closeCallBack={() => {
          setCardInfo(defaultCardInfo);
          setCardsDrawerVisible(false);
          props.initOrderInfo({
            resvNo,
          });
        }}
      />
      {cancelReservationList.length > 0 ? (
        <dl className={styles['settle-order-block']}>
          <dt className={commonStyles['bill-block-title']}>{intl.get('app_cancle_book')}</dt>
          <dd>
            <EditableTable
              rowKey="recNo"
              loading={orderInfoLoading}
              dataSource={cancelReservationList}
              columns={cancalResvColumns}
              saveDataList={(list) => saveModifiedCancalResvList(list)}
              pagination={false}
            />
          </dd>
        </dl>
      ) : null}
      {checkOutRoomList.length > 0 ? (
        <dl className={styles['settle-order-block']} style={{ marginTop: 40 }}>
          <dt className={commonStyles['bill-block-title']}>
            {intl.get('app_enum_room_status_checkout')}
          </dt>
          <dd>
            {roomCardEnabled ? (
              <div style={{ marginBottom: 24 }}>
                <Button type="primary" onClick={() => checkRoomCard()}>
                  {intl.get('drawer_checkout_room_card_return_btn')}
                </Button>
              </div>
            ) : null}
            <EditableTable
              rowKey="recNo"
              loading={orderInfoLoading}
              dataSource={checkOutRoomList}
              columns={checkOutRoomColumns}
              saveDataList={(list) => saveModifiedCheckoutRoomList(list)}
              pagination={false}
            />
          </dd>
        </dl>
      ) : null}
      <OvertimePriceAdjustList adjustReasons={adjustReasons || []} />
      <RenewalRecord ref={renewalRecordRef} />
    </div>
  );
}

const mapStateToProps = (state) => {
  const { orderInfoLoading, orderInfo } = state.settle;
  const { roomCardEnabled, enabledVendor } = state.card;
  return { orderInfoLoading, orderInfo, roomCardEnabled, enabledVendor };
};

export default connect(mapStateToProps, {
  initOrderInfo,
  updateOrderInfo,
  toggleOverStayModalVisible,
  initOverStayList,
})(CheckOut);
